import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import { useSeasonalityStore } from "../stores/seasonalityStore";
import { useAuthStore } from "../stores/authStore";
import type { 
  SeasonalityDashboardItem, 
  SeasonalityAlert, 
  UserSeasonalityPreferences 
} from "../types/database";

// ============================================
// SEASONALITY METRICS
// ============================================

export function useSeasonalityMetrics(region?: string, year?: number) {
  const { setMetrics, setIsLoading, selectedYear } = useSeasonalityStore();
  const targetYear = year || selectedYear;

  return useQuery({
    queryKey: ["seasonality-metrics", region, targetYear],
    queryFn: async () => {
      setIsLoading(true);
      
      let query = supabase
        .from("v_seasonality_dashboard")
        .select("*")
        .eq("year", targetYear)
        .order("month", { ascending: true });

      if (region) {
        query = query.eq("region", region);
      }

      const { data, error } = await query;

      if (error) throw error;
      
      const metrics = data as SeasonalityDashboardItem[];
      setMetrics(metrics);
      setIsLoading(false);
      return metrics;
    },
  });
}

export function useAllRegionsMetrics() {
  const { selectedYear } = useSeasonalityStore();

  return useQuery({
    queryKey: ["seasonality-all-regions", selectedYear],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_seasonality_dashboard")
        .select("*")
        .eq("year", selectedYear)
        .order("region", { ascending: true })
        .order("month", { ascending: true });

      if (error) throw error;
      return data as SeasonalityDashboardItem[];
    },
  });
}

export function useLowSeasonPeriods(months = 3) {
  return useQuery({
    queryKey: ["low-season-periods", months],
    queryFn: async () => {
      const now = new Date();
      const futureDate = new Date();
      futureDate.setMonth(futureDate.getMonth() + months);

      const { data, error } = await supabase
        .from("seasonality_metrics")
        .select("*")
        .eq("is_low_season", true)
        .gte("period_start", now.toISOString().split("T")[0])
        .lte("period_start", futureDate.toISOString().split("T")[0])
        .order("period_start", { ascending: true });

      if (error) throw error;
      return data;
    },
  });
}

// ============================================
// SEASONALITY ALERTS
// ============================================

export function useSeasonalityAlerts() {
  const { user } = useAuthStore();
  const { setAlerts } = useSeasonalityStore();

  return useQuery({
    queryKey: ["seasonality-alerts", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from("seasonality_alerts")
        .select("*")
        .eq("user_id", user.id)
        .eq("is_dismissed", false)
        .order("created_at", { ascending: false });

      if (error) throw error;
      
      const alerts = data as SeasonalityAlert[];
      setAlerts(alerts);
      return alerts;
    },
    enabled: !!user?.id,
  });
}

export function useDismissAlert() {
  const queryClient = useQueryClient();
  const { dismissAlert } = useSeasonalityStore();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("seasonality_alerts")
        .update({ is_dismissed: true })
        .eq("id", id);

      if (error) throw error;
      return id;
    },
    onSuccess: (id) => {
      dismissAlert(id);
      queryClient.invalidateQueries({ queryKey: ["seasonality-alerts"] });
    },
  });
}

// ============================================
// USER PREFERENCES
// ============================================

export function useSeasonalityPreferences() {
  const { user } = useAuthStore();
  const { setPreferences } = useSeasonalityStore();

  return useQuery({
    queryKey: ["seasonality-preferences", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from("user_seasonality_preferences")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle(); // Returns null instead of error when 0 rows

      if (error) throw error;
      
      const preferences = data as UserSeasonalityPreferences | null;
      setPreferences(preferences);
      return preferences;
    },
    enabled: !!user?.id,
  });
}

export function useUpdateSeasonalityPreferences() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const { updatePreferences } = useSeasonalityStore();

  return useMutation({
    mutationFn: async (updates: Partial<UserSeasonalityPreferences>) => {
      if (!user?.id) throw new Error("User not authenticated");

      const { data, error } = await supabase
        .from("user_seasonality_preferences")
        .upsert({
          user_id: user.id,
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) throw error;
      return data as UserSeasonalityPreferences;
    },
    onSuccess: (data) => {
      updatePreferences(data);
      queryClient.invalidateQueries({ queryKey: ["seasonality-preferences"] });
    },
  });
}

// ============================================
// REGION ANALYTICS
// ============================================

export function useRegionAnalytics(region: string) {
  return useQuery({
    queryKey: ["region-analytics", region],
    queryFn: async () => {
      // Get metrics for the region
      const { data: metrics, error: metricsError } = await supabase
        .from("v_seasonality_dashboard")
        .select("*")
        .eq("region", region)
        .order("year", { ascending: false })
        .order("month", { ascending: true })
        .limit(24); // Last 2 years

      if (metricsError) throw metricsError;

      // Get operator count for the region
      const { count: operatorCount, error: countError } = await supabase
        .from("operators")
        .select("*", { count: "exact", head: true })
        .eq("region", region);

      if (countError) throw countError;

      // Calculate averages and trends
      const avgDemand = metrics?.reduce((sum, m) => sum + (m.estimated_demand_index || 0), 0) / (metrics?.length || 1);
      const lowSeasonMonths = metrics?.filter(m => m.is_low_season).length || 0;

      return {
        metrics: metrics as SeasonalityDashboardItem[],
        operatorCount: operatorCount || 0,
        avgDemandIndex: avgDemand,
        lowSeasonMonths,
        bestMonth: metrics?.reduce((best, m) => 
          (m.estimated_demand_index || 0) > (best.estimated_demand_index || 0) ? m : best
        , metrics?.[0]),
        worstMonth: metrics?.reduce((worst, m) => 
          (m.estimated_demand_index || 0) < (worst.estimated_demand_index || 0) ? m : worst
        , metrics?.[0]),
      };
    },
    enabled: !!region,
  });
}

